import React from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useAuth } from '../auth';

export function Layout() {
  const { user, logout } = useAuth();
  const location = useLocation();

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const navItems = [
    { path: '/dashboard', label: 'Главная' },
    { path: '/search', label: 'Поиск людей' },
    { path: '/history', label: 'История' },
    ...(user?.role === 'Администратор' ? [{ path: '/admin', label: 'Админка' }] : []),
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header Navigation */}
      <header className="border-b bg-card">
        <div className="w-full px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Left side - Logo and Navigation */}
            <div className="flex items-center space-x-8">
              {/* Logo */}
              <Link to="/dashboard" className="text-2xl font-bold text-primary">
                RUTON
              </Link>
              
              {/* Navigation Menu */}
              <nav className="hidden md:flex space-x-6">
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`text-sm font-medium transition-colors hover:text-primary ${
                      isActive(item.path)
                        ? 'text-primary border-b-2 border-primary pb-1'
                        : 'text-muted-foreground'
                    }`}
                  >
                    {item.label}
                  </Link>
                ))}
              </nav>
            </div>

            {/* Right side - Balance and User */}
            <div className="flex items-center space-x-6">
              {user && (
                <>
                  {/* Balance Section */}
                  <div className="flex items-center space-x-3 bg-muted/50 rounded-lg px-4 py-2">
                    <div className="text-sm">
                      <span className="text-muted-foreground">Запросов</span>
                      <div className="font-semibold">{user.requests_count}</div>
                    </div>
                    {/* Скрываем кнопку пополнения для админов, инфинити и определенных пользователей */}
                    {!['Инфинити'].includes(user.role) && 
                     !['Hell', 'Osnova'].includes(user.username) && (
                      <Button size="sm" variant="default" asChild>
                        <Link to="/payment">Пополнить</Link>
                      </Button>
                    )}
                  </div>

                  {/* User Menu */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="flex items-center space-x-2 p-2">
                        <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                          <span className="text-primary-foreground text-sm font-medium">
                            {user.username.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuItem asChild>
                        <Link to="/profile">Личный кабинет</Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/apidocs">API</Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handleLogout}>
                        Выход
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              )}
            </div>
          </div>

          {/* Mobile Navigation */}
          <nav className="md:hidden mt-4 flex space-x-4 overflow-x-auto">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`text-sm font-medium whitespace-nowrap transition-colors hover:text-primary ${
                  isActive(item.path)
                    ? 'text-primary border-b-2 border-primary pb-1'
                    : 'text-muted-foreground'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="w-full px-6 py-6">
        <Outlet />
      </main>
    </div>
  );
}
